import React, { useCallback, useEffect, useRef, useState } from "react";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import JSZip from "jszip";
import StripeProductIconSvg, {
    STRIPE_ICON_SIZE,
    STRIPE_PRODUCT_ICONS,
} from "./StripeProductIconSvg";

const PREVIEW_PX = 168;
const ZIP_FILE = "stripe-product-icons.zip";

function triggerBlobDownload(blob, fileName) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

function serializeSvg(svgNode) {
    return new XMLSerializer().serializeToString(svgNode);
}

async function svgNodeToPngBlob(svgNode) {
    const svgBlob = new Blob([serializeSvg(svgNode)], {
        type: "image/svg+xml;charset=utf-8",
    });
    const url = URL.createObjectURL(svgBlob);
    try {
        const img = new Image();
        await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = () => reject(new Error("Failed to render SVG"));
            img.src = url;
        });
        const canvas = document.createElement("canvas");
        canvas.width = STRIPE_ICON_SIZE;
        canvas.height = STRIPE_ICON_SIZE;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
            throw new Error("Canvas not supported");
        }
        ctx.clearRect(0, 0, STRIPE_ICON_SIZE, STRIPE_ICON_SIZE);
        ctx.drawImage(img, 0, 0, STRIPE_ICON_SIZE, STRIPE_ICON_SIZE);
        return await new Promise((resolve, reject) => {
            canvas.toBlob(
                (blob) => {
                    if (blob) resolve(blob);
                    else reject(new Error("PNG export failed"));
                },
                "image/png",
                1,
            );
        });
    } finally {
        URL.revokeObjectURL(url);
    }
}

function IconCard({ product, busyKey, onDownloadSvg, onDownloadPng, registerRef, error }) {
    const frameRef = useRef(null);
    const busy = busyKey === product.variant;

    useEffect(() => {
        registerRef(product.variant, frameRef);
    }, [product.variant, registerRef]);

    return (
        <Box
            sx={{
                flex: "1 1 260px",
                minWidth: 240,
                maxWidth: 340,
                p: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
            }}
        >
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {product.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {product.summary}
            </Typography>
            <Box
                ref={frameRef}
                sx={{
                    width: PREVIEW_PX,
                    height: PREVIEW_PX,
                    mx: "auto",
                    mb: 1.5,
                    borderRadius: 2,
                    overflow: "hidden",
                    lineHeight: 0,
                    boxShadow: "0 6px 20px rgba(15,23,42,0.14)",
                    "& > svg": { width: PREVIEW_PX, height: PREVIEW_PX, display: "block" },
                }}
            >
                <StripeProductIconSvg variant={product.variant} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                {product.detail}
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="center">
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SaveAltIcon />}
                    disabled={Boolean(busyKey)}
                    onClick={() => onDownloadSvg(product)}
                    sx={{ textTransform: "none", fontWeight: 700 }}
                >
                    SVG
                </Button>
                <Button
                    size="small"
                    variant="contained"
                    startIcon={
                        busy ? <CircularProgress size={15} color="inherit" /> : <SaveAltIcon />
                    }
                    disabled={Boolean(busyKey)}
                    onClick={() => onDownloadPng(product)}
                    sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        bgcolor: "#635BFF",
                        "&:hover": { bgcolor: "#4F46E5" },
                    }}
                >
                    PNG {STRIPE_ICON_SIZE}
                </Button>
            </Stack>
            {error ? (
                <Typography
                    variant="caption"
                    color="error"
                    sx={{ display: "block", textAlign: "center", mt: 0.75 }}
                >
                    {error}
                </Typography>
            ) : null}
        </Box>
    );
}

/**
 * Stripe dashboard product images — preview + SVG / PNG download for each product.
 * Used by the admin Stripe tab and the stand-alone capture page.
 */
export default function StripeProductIconsPanel({ variants } = {}) {
    const products = Array.isArray(variants) && variants.length
        ? STRIPE_PRODUCT_ICONS.filter((item) => variants.includes(item.variant))
        : STRIPE_PRODUCT_ICONS;
    const [busyKey, setBusyKey] = useState(null);
    const [errors, setErrors] = useState({});
    const refsRef = useRef({});

    const registerRef = useCallback((variant, ref) => {
        refsRef.current[variant] = ref;
    }, []);

    const iconNode = useCallback(
        (variant) => refsRef.current[variant]?.current?.querySelector("svg") || null,
        [],
    );

    const setError = useCallback((key, message) => {
        setErrors((prev) => ({ ...prev, [key]: message }));
    }, []);

    const handleDownloadSvg = useCallback(
        (product) => {
            const node = iconNode(product.variant);
            if (!node) return;
            try {
                const blob = new Blob([serializeSvg(node)], {
                    type: "image/svg+xml;charset=utf-8",
                });
                triggerBlobDownload(blob, `${product.fileBase}.svg`);
                setError(product.variant, null);
            } catch (e) {
                setError(product.variant, e?.message || "Failed to download SVG");
            }
        },
        [iconNode, setError],
    );

    const handleDownloadPng = useCallback(
        async (product) => {
            const node = iconNode(product.variant);
            if (!node || busyKey) return;
            setBusyKey(product.variant);
            setError(product.variant, null);
            try {
                const blob = await svgNodeToPngBlob(node);
                triggerBlobDownload(blob, `${product.fileBase}.png`);
            } catch (e) {
                setError(product.variant, e?.message || "Failed to download PNG");
            } finally {
                setBusyKey(null);
            }
        },
        [busyKey, iconNode, setError],
    );

    const handleDownloadZip = useCallback(async () => {
        if (busyKey) return;
        setBusyKey("zip");
        setError("zip", null);
        try {
            const zip = new JSZip();
            for (const product of products) {
                const node = iconNode(product.variant);
                if (!node) continue;
                zip.file(`${product.fileBase}.svg`, serializeSvg(node));
                zip.file(`${product.fileBase}.png`, await svgNodeToPngBlob(node));
            }
            triggerBlobDownload(await zip.generateAsync({ type: "blob" }), ZIP_FILE);
        } catch (e) {
            setError("zip", e?.message || "Failed to build ZIP");
        } finally {
            setBusyKey(null);
        }
    }, [busyKey, iconNode, products, setError]);

    const showZip = products.length > 1;

    return (
        <Box>
            <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
                sx={{ mb: 2 }}
            >
                <Typography variant="body2" color="text.secondary">
                    Product images for the Stripe dashboard — square PNG, at least 128×128 (these
                    export at {STRIPE_ICON_SIZE}×{STRIPE_ICON_SIZE}). Upload on Stripe → Product
                    catalog → product → Edit product → image.
                </Typography>
                {showZip ? (
                    <Button
                        variant="outlined"
                        startIcon={
                            busyKey === "zip" ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <FolderZipIcon />
                            )
                        }
                        disabled={Boolean(busyKey)}
                        onClick={handleDownloadZip}
                        sx={{ textTransform: "none", fontWeight: 700, flexShrink: 0 }}
                    >
                        Download all
                    </Button>
                ) : null}
            </Stack>
            {errors.zip ? (
                <Typography variant="caption" color="error" sx={{ display: "block", mb: 1.5 }}>
                    {errors.zip}
                </Typography>
            ) : null}
            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                {products.map((product) => (
                    <IconCard
                        key={product.variant}
                        product={product}
                        busyKey={busyKey}
                        onDownloadSvg={handleDownloadSvg}
                        onDownloadPng={handleDownloadPng}
                        registerRef={registerRef}
                        error={errors[product.variant]}
                    />
                ))}
            </Stack>
        </Box>
    );
}
